import { useContext, useMemo } from "react";
import { useMatches } from "react-router-dom";

import { PageMetaContext, type PageMeta } from "@/lib/page-meta";

/**
 * Walks the matched route tree from outermost ancestor to current leaf,
 * collecting `handle.meta` from each match; later (more specific) matches
 * override earlier ones. Finally, any active `usePageMeta` override is
 * merged on top.
 *
 * This is the single source of truth for the topbar breadcrumbs and the
 * document.title.
 */
export function useResolvedPageMeta(): PageMeta {
  const matches = useMatches();
  const ctx = useContext(PageMetaContext);
  const override = ctx?.override ?? null;

  return useMemo(() => {
    let resolved: PageMeta = {};
    for (const m of matches) {
      const handleMeta = (m.handle as { meta?: PageMeta } | undefined)?.meta;
      if (handleMeta) resolved = mergeMeta(resolved, handleMeta);
    }
    if (override) resolved = mergeMeta(resolved, override);
    return resolved;
  }, [matches, override]);
}

/** Right-hand side wins for each present field. Breadcrumbs are replaced
 *  wholesale (not concatenated) — the most specific route owns the full
 *  trail. Children that want to extend a parent's trail should include the
 *  parent's crumbs explicitly. */
function mergeMeta(base: PageMeta, next: PageMeta): PageMeta {
  return {
    title: next.title ?? base.title,
    breadcrumbs: next.breadcrumbs ?? base.breadcrumbs,
  };
}
